import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiSearch, FiEdit2 } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { RiParentLine } from 'react-icons/ri';
import { formatDateFR } from '../utils/dateUtils';
import ParentModal from '../components/parents/ParentModal';
import ListSpinner from '../components/common/ListSpinner';
import MobileCard from '../components/common/MobileCard';
import MobileCardList from '../components/common/MobileCardList';

const limit = 25;

const parentMatchesSearch = (parent, term) => {
  if (!term) return false;
  const t = term.toLowerCase().trim();
  const fields = [
    parent.nom,
    parent.prenom,
    `${parent.prenom || ''} ${parent.nom || ''}`,
    `${parent.nom || ''} ${parent.prenom || ''}`,
    parent.email,
    parent.telmob1,
    parent.telmob2,
    parent.type_parente,
  ];
  return fields.some((f) => f && String(f).toLowerCase().includes(t));
};

const eleveMatchesSearch = (eleve, term) => {
  if (!term) return false;
  const t = term.toLowerCase().trim();
  const fields = [
    eleve.nom,
    eleve.prenom,
    `${eleve.prenom || ''} ${eleve.nom || ''}`,
    `${eleve.nom || ''} ${eleve.prenom || ''}`,
    eleve.matricule,
    eleve.telephone,
  ];
  return fields.some((f) => f && String(f).toLowerCase().includes(t));
};

const Parents = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [editingParent, setEditingParent] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, refetch } = useQuery(
    ['eleves-with-parents', debouncedSearch],
    () =>
      api
        .get(`/eleves/with-parents?search=${encodeURIComponent(debouncedSearch)}`)
        .then((res) => res.data),
    { keepPreviousData: true }
  );

  const parentsArray = Array.isArray(data?.data) ? data.data : [];
  const total = parentsArray.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const paginatedParents = parentsArray.slice((page - 1) * limit, page * limit);

  useEffect(() => {
    if (!debouncedSearch) return;
    const autoExpanded = {};
    paginatedParents.forEach((eleve) => {
      autoExpanded[eleve.id] = true;
    });
    setExpanded((prev) => ({ ...prev, ...autoExpanded }));
  }, [debouncedSearch, data, page]);

  const toggleExpand = (eleveId) => {
    setExpanded((prev) => ({ ...prev, [eleveId]: !prev[eleveId] }));
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const searchActive = debouncedSearch.length > 0;

  const visibleParents = useMemo(() => {
    return (eleve) => {
      const parents = eleve.parents || [];
      if (!searchActive) return parents;
      if (eleveMatchesSearch(eleve, debouncedSearch)) return parents;
      return parents.filter((p) => parentMatchesSearch(p, debouncedSearch));
    };
  }, [searchActive, debouncedSearch]);

  const handleEditParent = (e, parent) => {
    e.stopPropagation();
    setEditingParent(parent);
  };

  const handleParentSaved = () => {
    setEditingParent(null);
    refetch();
    toast.success('Parent mis à jour');
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Parents par élève</h1>
      </div>
      {/* Barre de recherche */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par élève, parent, email ou téléphone..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={search}
            onChange={handleSearch}
          />
        </div>
      </div>
      <p className="mb-3 text-sm text-gray-600 italic md:hidden">
        {searchActive
          ? `${total} élève(s) trouvé(s)`
          : 'Touchez un élève pour voir ses parents'}
      </p>

      {/* Vue mobile : mini-cards accordéon */}
      <div className="md:hidden">
        {isLoading ? (
          <ListSpinner />
        ) : (
          <MobileCardList
            isEmpty={parentsArray.length === 0}
            emptyMessage={searchActive ? 'Aucun élève ou parent correspondant' : 'Aucun élève trouvé'}
          >
            {paginatedParents.map((eleve) => (
              <div key={eleve.id}>
                <MobileCard
                  title={`${eleve.nom} ${eleve.prenom}`}
                  subtitle={eleve.matricule}
                  badge={
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
                      {eleve.parents?.length || 0} parent{(eleve.parents?.length || 0) > 1 ? 's' : ''}
                    </span>
                  }
                  fields={[
                    { label: 'Sexe', value: eleve.sexe },
                    { label: 'Naissance', value: formatDateFR(eleve.date_naissance) },
                    { label: 'Téléphone', value: eleve.telephone || '—' },
                  ]}
                  onClick={() => toggleExpand(eleve.id)}
                />
                {expanded[eleve.id] && (
                  <div className="mt-2 ml-1 space-y-2">
                    {eleve.parents?.length === 0 ? (
                      <div className="mobile-card-empty text-xs py-4">Aucun parent associé</div>
                    ) : visibleParents(eleve).length === 0 ? (
                      <div className="mobile-card-empty text-xs py-4">Aucun parent correspondant</div>
                    ) : (
                      visibleParents(eleve).map((parent) => (
                        <MobileCard
                          key={parent.id}
                          title={`${parent.prenom} ${parent.nom}`}
                          subtitle={parent.type_parente}
                          fields={[
                            { label: 'Tél.', value: parent.telmob1 || '—' },
                            { label: 'Email', value: parent.email || '—' },
                          ]}
                          actions={
                            <button
                              type="button"
                              onClick={(e) => handleEditParent(e, parent)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded"
                            >
                              <FiEdit2 size={14} /> Modifier
                            </button>
                          }
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </MobileCardList>
        )}
      </div>

      {/* Vue desktop : tableau */}
      <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto">
        <p className="px-6 pt-4 pb-2 text-sm text-gray-600 italic">
          {searchActive
            ? `${total} élève(s) trouvé(s) — parents correspondants affichés automatiquement`
            : 'Déroulez l\'élève pour afficher les parents'}
        </p>
        {isLoading ? (
          <div className="p-8 text-center">Chargement...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sexe</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date naissance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parents</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parentsArray.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    {searchActive ? 'Aucun élève ou parent correspondant' : 'Aucun élève trouvé'}
                  </td>
                </tr>
              ) : (
                paginatedParents.map((eleve) => (
                  <React.Fragment key={eleve.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(eleve.id)}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex shrink-0 items-center justify-center rounded-full p-1.5 ${expanded[eleve.id] ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-primary-600'}`}>
                            <RiParentLine size={18} />
                          </span>
                          <span>{eleve.nom} {eleve.prenom}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{eleve.matricule}</td>
                      <td className="px-6 py-4">{eleve.sexe}</td>
                      <td className="px-6 py-4">{formatDateFR(eleve.date_naissance)}</td>
                      <td className="px-6 py-4">{eleve.telephone}</td>
                      <td className="px-6 py-4">{eleve.parents?.length || 0}</td>
                    </tr>
                    {expanded[eleve.id] && (
                      <tr>
                        <td colSpan="6" className="bg-gray-50 px-8 py-4">
                          {eleve.parents?.length === 0 ? (
                            <div className="text-gray-500">Aucun parent associé</div>
                          ) : visibleParents(eleve).length === 0 ? (
                            <div className="text-gray-500">Aucun parent correspondant à la recherche</div>
                          ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prénom</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleParents(eleve).map((parent) => (
                                  <tr
                                    key={parent.id}
                                    className={searchActive && parentMatchesSearch(parent, debouncedSearch) ? 'bg-primary-50/60' : ''}
                                  >
                                    <td className="px-4 py-2">{parent.nom}</td>
                                    <td className="px-4 py-2">{parent.prenom}</td>
                                    <td className="px-4 py-2">{parent.type_parente}</td>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-1.5">
                                        <span>{parent.telmob1 || '—'}</span>
                                        {parent.telmob1_whatsapp ? (
                                          <FaWhatsapp className="h-3.5 w-3.5 text-[#25D366]" title="WhatsApp" />
                                        ) : null}
                                      </div>
                                      {parent.telmob2 && (
                                        <div className="flex items-center gap-1.5 mt-1 text-gray-600">
                                          <span>{parent.telmob2}</span>
                                          {parent.telmob2_whatsapp ? (
                                            <FaWhatsapp className="h-3.5 w-3.5 text-[#25D366]" title="WhatsApp" />
                                          ) : null}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">{parent.email || '—'}</td>
                                    <td className="px-4 py-2 text-right">
                                      <button
                                        type="button"
                                        onClick={(e) => handleEditParent(e, parent)}
                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded"
                                        title="Modifier le parent"
                                      >
                                        <FiEdit2 size={14} />
                                        Modifier
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {!isLoading && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Premier</button>
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Précédent</button>
          {(() => {
            const pages = [];
            if (totalPages <= 10) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              // 1 à 5, ..., totalPages-4 à totalPages
              for (let i = 1; i <= 5; i++) pages.push(i);
              if (page > 7 && page < totalPages - 6) {
                pages.push('...');
                for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                pages.push('...');
              } else {
                pages.push('...');
              }
              for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 5) pages.push(i);
              }
            }
            // Nettoyage et affichage
            return [...new Set(pages)].map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2">...</span>
                : <button
                    key={p}
                    className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-primary-100 text-gray-700'}`}
                    onClick={() => setPage(p)}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Suivant</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Dernier</button>
        </div>
      )}
      {editingParent && (
        <ParentModal
          parent={editingParent}
          onClose={() => setEditingParent(null)}
          onSuccess={handleParentSaved}
        />
      )}
    </div>
  );
};

export default Parents;
